/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import * as Leo from "@adobe/leonardo-contrast-colors";
import chroma from "chroma-js";
import buildColorScale from "components/ui/colors/buildColorScale";
import { tailwindColors } from "components/ui/colors/colorsBundle";
import orderColorsByLuminosity from "components/ui/colors/orderColorsByLuminosity";
import { Stack } from "datastructures-js";
import { invariant } from "outvariant";

const defaultBaseRatios = [-0.02, 1.1, 1.12, 1.25, 1.45, 1.75, 2.25, 3.01, 4.52, 7, 14.5];
export type ColorSchemeTheme = {
  theme: Leo.Theme;
  contrastColors: Record<ColorNames, Record<ColorScale, string>>;
  contrastColorsValues: Record<ColorNames, string[]>;
  genChartScale: (swatches: number, baseColor: ColorNames, from: ColorScale, to: ColorScale) => string[];
};
export type ColorSchemeThemeName = "dark" | "light";
type ColorRedirectSuggestion = {
  from: {
    name: ColorNames | null;
    scale: ColorScale | null;
    color: string;
  };
  to: {
    name: ColorNames | null;
    scale: ColorScale | null;
    color: string;
  } | null;
};
type ColorScheme = {
  base: {
    contrastColors: Record<ColorNames, Record<ColorScale, string>>;
    contrastColorsValues: Record<ColorNames, string[]>;
  };
  darkTheme: ColorSchemeTheme;
  lightTheme: ColorSchemeTheme;
  remapColor: (hex: string, theme?: ColorSchemeThemeName, overrideScale?: ColorScale | null) => string;
  shadingColor: (
    hex: string,
    index: number,
    theme?: ColorSchemeThemeName,
    padding?: number,
    swatches?: number,
  ) => string;
  redirectColors: (colors: string[], distanceThreshold?: number) => ColorRedirectSuggestion[];
  redirectColorsAsHex: (colors: string[], distanceThreshold?: number) => string[];
};

interface CreateColorSchemeProps {
  baseRatios?: number[];
  lightThemeBgColor?: Leo.CssColor;
  darkThemeBgColor?: Leo.CssColor;
  colorspace?: Leo.InterpolationColorspace;
}
export default function createColorScheme({
  baseRatios = defaultBaseRatios,
  lightThemeBgColor = "#fafafa",
  darkThemeBgColor = tailwindColors.slate[900] as Leo.CssColor,
  colorspace = "HSL",
}: CreateColorSchemeProps): ColorScheme {
  const baseColors: [string, Leo.CssColor[]][] = Object.entries(
    Object.entries(tailwindColors).reduce((state, [palette, colors]) => {
      return {
        ...state,
        [palette]: Object.values(colors),
      };
    }, {}),
  );

  const colors = baseColors.map(([name, colorKeys]) => {
    return new Leo.Color({
      name,
      colorKeys,
      colorspace,
      ratios: baseRatios,
    });
  });

  const lightTheme = new Leo.Theme({
    backgroundColor: new Leo.BackgroundColor({
      name: "light",
      colorKeys: [lightThemeBgColor],
      colorspace,
      ratios: baseRatios,
    }),
    colors,
    lightness: 97,
    contrast: 1,
    formula: "wcag2",
    output: "HEX",
  });
  const darkTheme = new Leo.Theme({
    backgroundColor: new Leo.BackgroundColor({
      name: "dark",
      colorKeys: [darkThemeBgColor],
      colorspace,
      ratios: baseRatios,
    }),
    colors,
    lightness: 15,
    contrast: 0.9,
    saturation: 100,
    formula: "wcag2",
    output: "HEX",
  });
  const renderTheme = (theme: Leo.Theme) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_bgColor, ...actualColors] = theme.contrastColors;
    const positions = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

    const contrastColors = actualColors.reduce(
      (chart, node) => {
        invariant(node.values.length === positions.length, "Unexpected number of values. It should match Tailwind's");
        return {
          ...chart,
          [node.name]: node.values.reduce((state, color, index) => {
            return {
              ...state,
              [positions[index]]: color.value,
            };
          }, {}),
        };
      },
      {} as ColorSchemeTheme["contrastColors"],
    );

    return {
      theme,
      contrastColors,
      contrastColorsValues: actualColors.reduce(
        (chart, node) => {
          return {
            ...chart,
            [node.name]: node.values.map((color) => color.value),
          };
        },
        {} as ColorSchemeTheme["contrastColorsValues"],
      ),
      genChartScale: (swatches: number, baseColor: ColorNames, from: ColorScale, to: ColorScale) => {
        const colorScale = buildColorScale(
          swatches,
          [tailwindColors[baseColor][from] as Leo.CssColor, tailwindColors[baseColor][to] as Leo.CssColor],
          "polynomial",
          "CAM02p",
        );
        invariant(colorScale.length === swatches, "Unexpected number of colors");
        invariant(Array.isArray(colorScale), "Unexpected chart scale type");
        invariant(
          colorScale.every((color) => typeof color === "string"),
          "Unexpected chart scale type",
        );
        return colorScale as unknown as string[];
      },
    };
  };
  const dark = renderTheme(darkTheme);
  const light = renderTheme(lightTheme);
  const tailwindColorsDatabase = new Map<string, [ColorNames, ColorScale]>();

  Object.entries(tailwindColors).forEach(([name, colors]) => {
    Object.entries(colors).forEach(([scale, hex]) => {
      invariant(typeof hex === "string", "Unexpected color type");
      if (tailwindColorsDatabase.has(hex)) {
        const [colorName, colorScale] = tailwindColorsDatabase.get(hex) as [ColorNames, ColorScale];
        invariant(
          colorScale === (parseInt(scale, 10) as unknown as ColorScale),
          "Unexpected color scale: %s / %s vs %s / %s",
          name,
          scale,
          colorName,
          colorScale,
        );
      }
      tailwindColorsDatabase.set(hex as string, [name as ColorNames, parseInt(scale, 10) as unknown as ColorScale]);
    });
  });

  return {
    base: {
      contrastColors: Object.entries(tailwindColors).reduce((state, [name, colors]) => {
        return {
          ...state,
          [name]: colors,
        };
      }, {}) as ColorSchemeTheme["contrastColors"],
      contrastColorsValues: baseColors.reduce((state, [name, colors]) => {
        return {
          ...state,
          [name]: colors,
        };
      }, {}) as ColorSchemeTheme["contrastColorsValues"],
    },
    darkTheme: dark,
    lightTheme: light,
    remapColor(hex, theme = "light", overrideScale = null) {
      const fromDatabase = tailwindColorsDatabase.get(hex);
      if (!fromDatabase) {
        // Instead of throwing...
        return hex;
      }
      const [name, _scale] = fromDatabase;
      const scale = overrideScale !== null ? overrideScale : _scale;
      return theme === "dark" ? dark.contrastColors[name][scale] : light.contrastColors[name][scale];
    },
    shadingColor(hex, index, theme = "light", padding: number = 2, swatches: number = 8) {
      const fromDatabase = tailwindColorsDatabase.get(hex);
      if (!fromDatabase) {
        // Instead of throwing...
        return hex;
      }
      const [name] = fromDatabase;
      if (theme === "light") {
        const colorSet = orderColorsByLuminosity(light.genChartScale(swatches, name, 400, 800), "toLight");
        return colorSet[(index + padding) % colorSet.length];
      }

      const colorSet = orderColorsByLuminosity(dark.genChartScale(swatches, name, 400, 900), "toLight");
      return colorSet[(index + padding) % colorSet.length];
    },
    redirectColors(colors, distanceThreshold = 13) {
      return redirectColors(tailwindColorsDatabase, colors, distanceThreshold);
    },
    redirectColorsAsHex(colors, distanceThreshold = 13) {
      const suggestions = redirectColors(tailwindColorsDatabase, colors, distanceThreshold);
      return suggestions.map((suggestion) => {
        return suggestion.to ? suggestion.to.color : suggestion.from.color;
      });
    },
  };
}

function redirectColors(
  tailwindColorsDatabase: Map<string, [ColorNames, ColorScale]>,
  colors: string[],
  distanceThreshold: number,
): ColorRedirectSuggestion[] {
  const redirectingColors = new Map<string, number[]>();
  colors.forEach((color, index) => {
    const currentPositions = redirectingColors.get(color);
    if (currentPositions) {
      redirectingColors.set(color, [...currentPositions, index]);
    } else {
      redirectingColors.set(color, [index]);
    }
  });

  const redirectStackChart = createColorsRedirectChart(distanceThreshold);

  return colors.map((color, index) => {
    const fromDatabase = tailwindColorsDatabase.get(color);
    const theSameColor = {
      from: {
        name: fromDatabase ? fromDatabase[0] : null,
        color,
        scale: fromDatabase ? fromDatabase[1] : null,
      },
      to: null,
    };
    const positions = redirectingColors.get(color);
    // We don't need to do anything as it's a unique color
    if (positions?.length === 1) {
      return theSameColor;
    }
    // Skip if it's the 1st appearance of this color
    if (positions?.[0] === index) {
      return theSameColor;
    }
    if (!fromDatabase) {
      return theSameColor;
    }

    const [name, originalScale] = fromDatabase;
    const redirectStack = redirectStackChart.get(name);
    if (!redirectStack) {
      return theSameColor;
    }

    if (redirectStack.isEmpty()) {
      return theSameColor;
    }
    const node = redirectStack.pop();
    if (node === color) {
      return theSameColor;
    }
    const replacement = tailwindColorsDatabase.get(node);
    if (!replacement) {
      return theSameColor;
    }
    const [suggestion] = replacement;
    if (suggestion === name) {
      return theSameColor;
    }
    const sameScale = tailwindColors[suggestion][originalScale];
    return {
      from: {
        name,
        scale: originalScale,
        color,
      },
      to: {
        name: suggestion,
        scale: originalScale,
        color: sameScale,
      },
    };
  });
}

export type ColorNames = keyof typeof tailwindColors;
export type ColorScale = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

export const defaultColorScheme = createColorScheme({});

function createColorsRedirectChart(deltaE = 10) {
  const strongColors = Object.entries(tailwindColors).reduce((state, [palette, colors]) => {
    return state.set(palette, colors[400]);
  }, new Map<string, string>());
  const redirectStack = new Map<string, Stack<string>>();
  for (const [palette, currentColor] of strongColors.entries()) {
    for (const brotherColor of strongColors.values()) {
      if (currentColor === brotherColor) {
        continue;
      }
      const distance = Math.ceil(chroma.deltaE(currentColor, brotherColor));
      if (distance > deltaE) {
        continue;
      }
      if (!redirectStack.has(palette)) {
        redirectStack.set(palette, new Stack([currentColor, brotherColor]));
        continue;
      }
      const stack = redirectStack.get(palette);
      if (stack) {
        stack.push(brotherColor);
      }
    }
  }
  return redirectStack;
}
